import COS from 'cos-js-sdk-v5'
import { lib, MD5 } from 'crypto-js'
import FileSaver from 'file-saver'
import mime from 'mime'

export type TokenData = {
  TmpSecretId: string
  TmpSecretKey: string
  Token: string
  startTime: number
  expiredTime: number
  Bucket: string
  Region: string
}

export type TypeFileResult = {
  key: string
  fileHash: string
  fileSize: number
  fileType: string
  fileName: string
}

export abstract class FileHelper {
  constructor(public prefix: string) {}

  /** 获取文件MD5 */
  static getFileMd5(file: File) {
    return new Promise<string | undefined>((resolve) => {
      try {
        const reader = new FileReader()
        reader.onload = function (e) {
          const fileContent = e.target?.result
          if (fileContent) {
            const wordArray = lib.WordArray.create(fileContent as any)
            const hash = MD5(wordArray).toString()
            resolve(hash)
          } else {
            resolve(undefined)
          }
        }
        reader.readAsArrayBuffer(file)
      } catch {
        resolve(undefined)
      }
    })
  }

  // 将图片转换为png
  static imgToPng(blob: Blob) {
    return new Promise<Blob | null>((resolve, reject) => {
      let img = document.createElement('img')
      img.onload = () => {
        let canvas = document.createElement('canvas')
        let ctx = canvas.getContext('2d')
        if (ctx) {
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0, img.width, img.height)
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create blob'))
            }
          }, 'image/png')
        } else {
          reject(new Error('Failed to get canvas context'))
        }
      }
      img.onerror = reject
      img.src = URL.createObjectURL(blob)
    })
  }

  private async getCos(
    type: 'upload' | 'download',
    key?: string
  ): Promise<[COS | null, TokenData | null]> {
    const data =
      type === 'upload'
        ? await this.getUploadToken()
        : key
        ? await this.getDownloadToken(key)
        : null
    if (!data) return [null, null]

    return [
      new COS({
        SecretId: data.TmpSecretId,
        SecretKey: data.TmpSecretKey,
        SecurityToken: data.Token,
        StartTime: data.startTime,
        ExpiredTime: data.expiredTime,
      }),
      data,
    ]
  }

  /** 上传文件 */
  async upload(file: File): Promise<TypeFileResult | undefined> {
    const [cos, data] = await this.getCos('upload')
    if (!cos || !data) return

    const fileMd5 = await FileHelper.getFileMd5(file)
    if (!fileMd5) return

    const extension = mime.getExtension(file.type) ?? file.name?.split('.')[1]
    const ext = extension ? `.${extension}` : ''
    const Key = `${this.prefix}/${fileMd5}${ext}`

    const result = await cos.uploadFile({
      Bucket: data.Bucket,
      Region: data.Region,
      Key,
      Body: file,
    })

    if (result.statusCode !== 200) return

    return {
      key: Key,
      fileHash: fileMd5,
      fileSize: file.size,
      fileType: extension,
      fileName: file.name,
    }
  }

  /** 批量上传文件 */
  async batchUpload(
    files: File[],
    onFileFinish: (data: TypeFileResult & { success: boolean }) => void
  ) {
    const [cos, data] = await this.getCos('upload')
    if (!cos || !data) return

    return await Promise.all(
      files.map(async (item) => {
        const fileMd5 = await FileHelper.getFileMd5(item)
        if (!fileMd5) return { success: false }

        const extension = mime.getExtension(item.type) ?? item.name?.split('.')[1]
        const ext = extension ? `.${extension}` : ''
        const Key = `${this.prefix}/${fileMd5}${ext}`

        const fileInfo: TypeFileResult = {
          key: Key,
          fileHash: fileMd5,
          fileSize: item.size,
          fileType: extension,
          fileName: item.name,
        }

        const result = await cos.uploadFile({
          Bucket: data.Bucket,
          Region: data.Region,
          Key,
          Body: item,
        })

        return onFileFinish({
          ...fileInfo,
          success: result.statusCode === 200,
        })
      })
    )
  }

  /** 下载文件 */
  async download(key: string, filename?: string) {
    const [cos, data] = await this.getCos('download', key)
    if (!cos || !data) return

    cos.getObjectUrl(
      {
        Bucket: data.Bucket,
        Region: data.Region,
        Key: key,
        Sign: true,
      },
      function (err, data) {
        if (err) return console.log(err)
        var downloadUrl =
          data.Url +
          (data.Url.indexOf('?') > -1 ? '&' : '?') +
          'response-content-disposition=attachment' // 补充强制下载的参数
        downloadUrl += `;filename=${filename}`
        FileSaver.saveAs(downloadUrl, filename)
      }
    )
  }

  /** 获取文件内容 */
  async getFileContent(
    key: string,
    type: 'blob' | 'text' = 'blob',
    onProgress?: (progressData: any) => void
  ): Promise<Blob | string | undefined> {
    const [cos, data] = await this.getCos('download', key)
    if (!cos || !data) return

    const result = await cos.getObject({
      Bucket: data.Bucket,
      Region: data.Region,
      Key: key,
      DataType: type, // 非必须
      onProgress,
    })

    return result?.Body as Blob
  }

  // 复制图片
  async copyImgToClipboard(src: string) {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const isSvg = /\.(svg)$/.test(src)

        this.getFileContent(src, isSvg ? 'text' : 'blob')
          .then(async (content) => {
            if (!content) return reject('复制失败，请下载使用')
            if (content instanceof Blob) {
              const blob = await FileHelper.imgToPng(content)
              if (!blob) return reject('复制失败，请下载使用')

              const data = [new ClipboardItem({ [blob.type]: blob })]
              return navigator.clipboard.write(data)
            } else {
              return navigator.clipboard.writeText(content)
            }
          })
          .then(() => {
            console.log('copyImgToClipboard success')
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
      } catch (error) {
        console.log(error)
        reject(error)
      }
    })
  }

  /** 获取上传令牌 */
  abstract getUploadToken(): Promise<TokenData | null>

  abstract getDownloadToken(key: string): Promise<TokenData | null>
}
